<template>
  <app-drawer :model-value="modelValue"
              size="sm"
              :persistent="false"
              :hide-footer="!hasFooter"
              :tabs="tabs"
              :tab="tab"
              @update:tab="$emit('update:tab', $event)"
              @update:model-value="$emit('close')"
              @cancel="$emit('close')"
  >
    <template #title>{{ $t('common.VersioningDrawer.title') }}</template>

    <template #tab-manage>
      <!-- @slot version management for the selected document -->
      <slot name="manage"></slot>
    </template>

    <template #tab-history>
      <!-- @slot version history of the selected document -->
      <slot name="history"></slot>
    </template>

    <!-- Fixed at the bottom, as the design system requires, so only the body scrolls. The
         history tab has no action of its own, and hideFooter then removes the bar entirely. -->
    <template #footer>
      <app-button type="ghost"
                  variant="neutral"
                  data-locator="cancel-button"
                  @click="$emit('close')"
      >
        {{ $t('common.cancel') }}
      </app-button>
      <app-button :disabled="addDisabled"
                  :loading="addLoading"
                  data-locator="confirm-button"
                  @click="$emit('add')"
      >
        {{ $t('common.add') }}
      </app-button>
    </template>
  </app-drawer>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import type { AppDrawerTab } from '@/design-system/drawers/types'

const MANAGE_TAB = 'manage'
const HISTORY_TAB = 'history'

type VersionedDocument = {
  type?: string
  nbVersions?: number
}

/**
 * Shell shared by the two versioning panes, the documents one and the checklist one: an
 * AppDrawer sized and configured the same way, owning the manage / history tabs. Consumers
 * only bring their locator, their two tab contents and their store wiring.
 *
 * The tab list is computed here rather than passed in, because both panes derived it from the
 * exact same three conditions, down to identical labels. A single selected file can be managed
 * by whoever may upload versions, and its history is visible either to them or to a guest with
 * more than one version to look at.
 *
 * The footer is the drawer's own, so it stays fixed while the body scrolls, as the design
 * system requires. It only exists on the management tab: adding a version is the one action
 * that applies to the whole drawer, while the history offers its actions per version. Its
 * state is driven from outside, since the form that decides it lives in DocumentVersioning.
 *
 * Closing is reported as a single `close` event, as in FiltersDrawer and ShareDrawer: neither
 * pane distinguishes the header button from an outside interaction.
 *
 * `data-locator` is not declared as a prop: it falls through to the drawer, which forwards it
 * through useDataLocator like every other consumer.
 *
 * @displayName VersioningDrawer
 */
export default defineComponent({
  name: 'VersioningDrawer',
  props: {
    /**
     * @model
     * Visibility state of the drawer
     */
    modelValue: {
      type: Boolean,
      default: false,
    },
    /**
     * Documents the pane was opened on. Versioning only applies to a single file, so any other
     * selection leaves the drawer without tabs.
     */
    documents: {
      type: Array as PropType<VersionedDocument[]>,
      default: () => [],
    },
    /**
     * Whether the current user may add versions, which also unlocks the full history
     */
    canManageVersions: {
      type: Boolean,
      default: false,
    },
    /**
     * @model
     * Active tab value (use with v-model:tab)
     */
    tab: {
      type: [String, Number] as PropType<string | number | undefined>,
      default: undefined,
    },
    /**
     * Blocks the add action, typically while no file has been picked yet
     */
    addDisabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Set loading state of the add action
     */
    addLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['add', 'close', 'update:tab'],
  computed: {
    /**
     * Only the management tab acts on the document; the history tab offers its actions per
     * version, inside the body.
     */
    hasFooter (): boolean {
      return this.tab === MANAGE_TAB
    },
    selectedFile (): VersionedDocument | null {
      const [document] = this.documents

      return this.documents.length === 1 && document?.type === 'file' ? document : null
    },
    tabs (): AppDrawerTab[] {
      if (!this.selectedFile) {
        return []
      }

      const tabs: AppDrawerTab[] = []

      if (this.canManageVersions) {
        tabs.push({ label: this.$t('common.VersioningDrawer.tabManage'), value: MANAGE_TAB })
      }

      if (this.canManageVersions || (this.selectedFile.nbVersions ?? 0) > 1) {
        tabs.push({
          label: this.canManageVersions
            ? this.$t('common.VersioningDrawer.tabHistory')
            : this.$t('common.VersioningDrawer.tabHistoryGuests'),
          value: HISTORY_TAB,
        })
      }

      return tabs
    },
  },
})
</script>
